import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    LinearProgress,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import React, { useRef, useState } from 'react';
import { PromotionalBalanceService } from '../../services/PromotionalBalanceService';
import EventBus from '../../EventBus';
import { EventBusEventsEnum } from '../../enums/EventBusEventsEnum';
import { CustomSnackbarTypes } from '../Common/CustomSnackBar';
import { useNavigate } from 'react-router-dom';

const SAMPLE_CSV_CONTENT = `sno,customerId,amount,expiryDate\n1,12345,100,2026-12-31\n2,67890,200,2026-12-31`;

const downloadSampleCsv = () => {
    const blob = new Blob([SAMPLE_CSV_CONTENT], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'promotional_balance_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
};

const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const promotionalBalanceService = new PromotionalBalanceService();

const STEPS = [
    { num: 1, label: 'Campaign Name' },
    { num: 2, label: 'Upload CSV' },
    { num: 3, label: 'Submit' },
];

const CSV_COLUMNS = ['sno', 'customerId', 'amount', 'expiryDate'];

const BulkUploadPromotionalBalance = () => {
    const [campaignName, setCampaignName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadStep, setUploadStep] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (f) setFile(f);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f && f.name.endsWith('.csv')) setFile(f);
    };

    const handleSubmit = async () => {
        if (!campaignName.trim()) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: 'Campaign name is required', type: CustomSnackbarTypes.ERROR });
            return;
        }
        if (!file) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: 'Please select a CSV file', type: CustomSnackbarTypes.ERROR });
            return;
        }

        setIsSubmitting(true);
        try {
            setUploadStep('Getting upload URL…');
            const fileName = `${campaignName.trim().replace(/\s+/g, '_')}_${Date.now()}.csv`;
            const { putUrl, getUrl } = await promotionalBalanceService.getSignedUrlForCohort(fileName);

            setUploadStep('Uploading file to storage…');
            await promotionalBalanceService.uploadCohortFileToGCS(putUrl, file);

            setUploadStep('Creating campaign…');
            await promotionalBalanceService.createCohort(campaignName.trim(), getUrl);

            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: `Campaign '${campaignName}' created and queued for processing`,
                type: CustomSnackbarTypes.SUCCESS,
            });
            navigate('/BulkPromotionalBalance');
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to create campaign';
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, { message: msg, type: CustomSnackbarTypes.ERROR });
        } finally {
            setIsSubmitting(false);
            setUploadStep('');
        }
    };

    const activeStep = !campaignName.trim() ? 1 : !file ? 2 : 3;

    return (
        <Box sx={{ maxWidth: 600, mt: 2 }}>
            <Card variant="outlined">
                <CardContent sx={{ p: 3 }}>
                    {/* Header */}
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                        <Typography variant="subtitle1" fontWeight={600}>
                            Bulk Upload — Promotional Balance
                        </Typography>
                        <Tooltip title="Download sample CSV">
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={downloadSampleCsv}
                                sx={{ textTransform: 'none' }}
                            >
                                Sample CSV
                            </Button>
                        </Tooltip>
                    </Box>

                    {/* Step indicators */}
                    <Stack direction="row" spacing={1} mb={3}>
                        {STEPS.map((s) => (
                            <Chip
                                key={s.num}
                                label={`${s.num}. ${s.label}`}
                                size="small"
                                color={activeStep === s.num ? 'primary' : activeStep > s.num ? 'success' : 'default'}
                                variant={activeStep === s.num ? 'filled' : 'outlined'}
                            />
                        ))}
                    </Stack>

                    <Divider sx={{ mb: 3 }} />

                    {/* Step 1 — Campaign Name */}
                    <Box mb={3}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} display="block" mb={1}>
                            STEP 1 · Campaign Name
                        </Typography>
                        <TextField
                            label="Campaign Name *"
                            value={campaignName}
                            onChange={(e) => setCampaignName(e.target.value)}
                            size="small"
                            fullWidth
                            disabled={isSubmitting}
                            placeholder="e.g. Diwali_2026"
                            helperText="Must be unique across all campaigns"
                        />
                    </Box>

                    {/* Step 2 — CSV Upload */}
                    <Box mb={3}>
                        <Typography variant="caption" color="text.secondary" fontWeight={500} display="block" mb={1}>
                            STEP 2 · Upload CSV
                        </Typography>

                        {/* Drop zone */}
                        <Box
                            onClick={() => !isSubmitting && fileInputRef.current?.click()}
                            onDrop={handleDrop}
                            onDragOver={(e) => e.preventDefault()}
                            sx={{
                                border: '2px dashed',
                                borderColor: file ? 'success.main' : 'divider',
                                borderRadius: 2,
                                p: 3,
                                textAlign: 'center',
                                cursor: isSubmitting ? 'default' : 'pointer',
                                bgcolor: file ? 'success.50' : 'grey.50',
                                transition: 'all 0.2s',
                                '&:hover': !isSubmitting ? { borderColor: 'primary.main', bgcolor: 'primary.50' } : {},
                            }}
                        >
                            {file ? (
                                <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
                                    <InsertDriveFileIcon color="success" />
                                    <Box textAlign="left">
                                        <Typography variant="body2" fontWeight={600}>{file.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{formatBytes(file.size)}</Typography>
                                    </Box>
                                    <Button
                                        size="small"
                                        variant="text"
                                        onClick={(e) => { e.stopPropagation(); setFile(null); }}
                                        disabled={isSubmitting}
                                        sx={{ ml: 1 }}
                                    >
                                        Change
                                    </Button>
                                </Stack>
                            ) : (
                                <Stack alignItems="center" spacing={1}>
                                    <UploadFileIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
                                    <Typography variant="body2" color="text.secondary">
                                        Drag & drop a CSV or <strong>click to browse</strong>
                                    </Typography>
                                </Stack>
                            )}
                        </Box>
                        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileChange} hidden />

                        {/* Column hint */}
                        <Box mt={1} display="flex" gap={0.5} flexWrap="wrap">
                            <Typography variant="caption" color="text.secondary">Required columns:</Typography>
                            {CSV_COLUMNS.map((col) => (
                                <Chip key={col} label={col} size="small" sx={{ height: 18, fontSize: 11 }} />
                            ))}
                        </Box>
                    </Box>

                    {/* Upload progress */}
                    {isSubmitting && (
                        <Box mb={2}>
                            <LinearProgress sx={{ borderRadius: 1, mb: 1 }} />
                            <Typography variant="caption" color="text.secondary">{uploadStep}</Typography>
                        </Box>
                    )}

                    {/* Submit */}
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !campaignName.trim() || !file}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        {isSubmitting ? uploadStep || 'Uploading…' : 'Create Campaign & Queue'}
                    </Button>
                </CardContent>
            </Card>
        </Box>
    );
};

export default BulkUploadPromotionalBalance;
